/**
 * Monocore product card — shared interactivity
 *
 * Loaded globally from layout/theme.liquid. No-op if the page has no
 * .mccl__card elements. Exposes:
 *   - window.mcclSelectSize, mcclColorHover, mcclColorLeave,
 *     mcclOpenSizeSheet, mcclAddToCart (called from inline onclick/onmouse*
 *     attributes emitted by snippets/monocore-product-card.liquid, and by
 *     the size bottom-sheet).
 *   - window.Monocore.card.{renderAll, setupGallery} for sections to call
 *     after appending new cards (e.g. collection's Load More).
 *
 * Depends on window.Monocore.cart from snippets/monocore-cart.liquid.
 */
(function () {
  'use strict';
  if (!window.Monocore) window.Monocore = {};

  // ── Helpers ──────────────────────────────────────────────────────
  function selectedVariant(sizId) {
    var wrap = document.getElementById(sizId);
    if (!wrap) return null;
    var sel = wrap.querySelector('.mccl__size.is-selected');
    return sel ? sel.getAttribute('data-variant-id') : null;
  }

  // ── Per-card UI render based on Monocore.cart state ─────────────
  /* The ATC button is the card's only cart control (the in-card qty counter was
     removed 2026-07-06 — quantity management lives in the KwikCart drawer, which
     auto-opens on every add). The button always reads "Add to Cart" and reopens
     the size sheet; the only cart-state it reflects is a transient "Adding…"
     while the selected variant's op is in flight. */
  function renderCardAtc(atcWrap) {
    if (!atcWrap) return;
    var card = atcWrap.closest('.mccl__card');
    if (!card) return;
    if (card.classList.contains('mccl__card--soldout')) return; /* disabled "Sold Out" button — never touch */
    var addBtn = atcWrap.querySelector('.mccl__add-btn');
    if (!addBtn) return;
    var sizesWrap = card.querySelector('.mccl__sizes');
    var selBtn = sizesWrap ? sizesWrap.querySelector('.mccl__size.is-selected') : null;
    var vid = selBtn ? selBtn.getAttribute('data-variant-id') : null;
    var pending = vid ? window.Monocore.cart.isPending(vid) : false;
    if (pending) {
      addBtn.disabled = true; addBtn.style.opacity = '0.7'; addBtn.textContent = 'Adding…';
    } else {
      addBtn.disabled = false; addBtn.style.opacity = ''; addBtn.textContent = 'Add to Cart';
    }
  }
  function renderAllCards() {
    document.querySelectorAll('.mccl__atc-wrap').forEach(renderCardAtc);
  }
  function renderCardForSizes(sizId) {
    var wrap = document.getElementById(sizId);
    if (!wrap) return;
    var card = wrap.closest('.mccl__card');
    if (!card) return;
    var atcWrap = card.querySelector('.mccl__atc-wrap');
    if (atcWrap) renderCardAtc(atcWrap);
  }

  // ── Size selection (called from inline onclick) ─────────────────
  window.mcclSelectSize = function (btn, sizId) {
    if (btn.classList.contains('is-unavailable')) return;
    var wrap = document.getElementById(sizId);
    if (!wrap) return;
    wrap.querySelectorAll('.mccl__size').forEach(function (b) { b.classList.remove('is-selected'); });
    btn.classList.add('is-selected');
    renderCardForSizes(sizId);
  };

  // ── Color label hover (called from inline onmouseenter/leave) ───
  window.mcclColorHover = function (swatch) {
    var label = document.getElementById(swatch.getAttribute('data-col-id'));
    if (label) label.textContent = swatch.getAttribute('data-color');
  };
  window.mcclColorLeave = function (swatch, original) {
    var label = document.getElementById(swatch.getAttribute('data-col-id'));
    if (label) label.textContent = original;
  };

  // ── Add to cart ──────────────────────────────────────────────────
  window.mcclAddToCart = function (addId, sizId) {
    var vid = selectedVariant(sizId);
    if (!vid) {
      var wrap = document.getElementById(sizId);
      if (wrap) { wrap.style.outline = '1.5px solid #c0392b'; setTimeout(function () { wrap.style.outline = ''; }, 900); }
      return;
    }
    var btn = document.getElementById(addId);
    if (!btn) return;
    btn.disabled = true; btn.textContent = 'Adding…'; btn.style.opacity = '0.7';
    if (!window.Monocore || !window.Monocore.cart) {
      btn.disabled = false; btn.style.opacity = ''; btn.textContent = 'Add to Cart';
      return;
    }
    window.Monocore.cart.add(vid).catch(function () {
      btn.disabled = false; btn.style.opacity = ''; btn.textContent = 'Add to Cart';
    });
  };

  // ── Size bottom-sheet ────────────────────────────────────────────
  // Markup lives in snippets/monocore-size-sheet.liquid (rendered globally from
  // layout/theme.liquid). Two entry points share one engine:
  //   mcclOpenSizeSheet(addId, sizId) — product cards; rows are built from the
  //     card's hidden .mccl__size pills (label / variant id / cap / unit price).
  //   mcszOpenData(data)              — KwikCart upsell cards (shadow DOM, no
  //     .mccl__* markup); the caller passes the same fields as plain data.
  //
  // BEHAVIOUR (2026-08-25, Mat-approved prototype .claude/preview/size-sheet-multi):
  // a tap on ADD adds one unit and the sheet STAYS OPEN — the row becomes a qty
  // stepper and a bag footer appears (count, total, KEEP SHOPPING | VIEW BAG).
  // Multiple sizes and multiple units of one size are therefore one tap each.
  // Quantity always goes through Monocore.cart, so caps, optimistic state, the
  // per-variant request queue and the rejection toast are unchanged.
  //
  // KwikCart interaction: GoKwik patches window.fetch and opens its drawer on any
  // /cart/add.js. We do not fight that — on the first add the sheet is elevated
  // above the drawer (.mcsz--over-drawer), so the drawer opens, populates and
  // re-renders BEHIND the sheet. VIEW BAG then just closes the sheet to reveal an
  // already-correct drawer; KEEP SHOPPING closes the sheet and the drawer with it,
  // unless the drawer was already open before the sheet (the upsell path), where
  // dismissing must return the shopper to the drawer they came from.
  var sheetCtx = null;
  /* sheetCtx while open:
     { items: [{label, variantId, available, max, price, pill}],
       nodes: { <variantId>: {row, left, num, minus, plus} },
       sizId, addId,      // card path only — keeps the card's pill state coherent
       opener,            // focus is restored here on dismissal
       drawerWasOpen }    // drawer already open when we opened → do not close it  */

  function sheetRoot() { return document.getElementById('mcsz'); }
  function cartApi() { return (window.Monocore && window.Monocore.cart) ? window.Monocore.cart : null; }
  function sheetQty(vid) { var c = cartApi(); return c ? (c.getQty(vid) || 0) : 0; }

  /* ₹ with the store's 2-decimal format (matches the `money` filter output the
     card and the sheet header already render). */
  function sheetMoney(cents) {
    var n = (parseInt(cents, 10) || 0) / 100;
    try { return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
    catch (e) { return '₹' + n.toFixed(2); }
  }

  // ── KwikCart drawer bridge ───────────────────────────────────────
  function drawerIsOpen() {
    var host = document.querySelector('shadow-dom-container');
    if (!host || !host.shadowRoot) return false;
    return !!host.shadowRoot.querySelector('.sidecart:not(.mc-dismiss)');
  }
  function drawerOpen() {
    if (typeof window.openGokwikSideCart === 'function') { window.openGokwikSideCart(); return true; }
    return false;
  }
  function drawerClose() {
    if (typeof window.closeGokwikSideCart === 'function') { window.closeGokwikSideCart(); return true; }
    return false;
  }

  // ── Open / close ─────────────────────────────────────────────────
  function sheetIsOpen() {
    var root = sheetRoot();
    return !!(root && root.classList.contains('is-open'));
  }

  /* mode: 'dismiss' (✕ / backdrop / Escape / KEEP SHOPPING) or 'bag' (VIEW BAG). */
  function sheetClose(mode) {
    var root = sheetRoot();
    if (!root || !sheetIsOpen()) return;
    var ctx = sheetCtx;
    sheetCtx = null;

    root.classList.remove('is-open');
    root.classList.remove('mcsz--over-drawer');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('mcsz-lock');
    document.body.classList.remove('mcsz-lock');

    if (mode === 'bag') {
      /* The drawer is normally already open behind the sheet (GoKwik opened it on
         our first add) — closing the sheet reveals it. Only ask for it if it is not. */
      if (!drawerIsOpen()) drawerOpen();
      return; /* the drawer owns focus from here */
    }

    /* Dismissal: take the drawer with us if OUR adds are what opened it. */
    if (ctx && !ctx.drawerWasOpen && drawerIsOpen()) drawerClose();

    var opener = ctx && ctx.opener;
    if (opener && opener.offsetParent && typeof opener.focus === 'function') opener.focus();
  }

  // ── Quantity ─────────────────────────────────────────────────────
  /* The drawer is only raised over when it is actually on screen. GoKwik's own
     ATC handling is enabled, so a theme-driven /cart/add.js does NOT auto-open it
     (verified on the live theme 2026-08-25) — but the app config can change, so
     after an add we look twice and elevate only if it did appear. Staying at
     z-index 900 in the normal case keeps the sheet below the rejection toast and
     below the KwikPass login layer. */
  function sheetWatchDrawer() {
    [400, 1200].forEach(function (ms) {
      setTimeout(function () {
        var root = sheetRoot();
        if (root && sheetIsOpen() && drawerIsOpen()) root.classList.add('mcsz--over-drawer');
      }, ms);
    });
  }

  function sheetSetQty(item, next) {
    var c = cartApi();
    if (!c || !sheetCtx) return;
    if (sheetSettling()) return;   /* rows are still moving — swallow the tap */
    /* A removal is in flight: Monocore.cart deliberately keeps the optimistic qty
       until the server confirms, so without this a second tap would fire a second
       /cart/change.js for the same line. */
    if (c.isRemoving && c.isRemoving(item.variantId)) return;
    var cur = sheetQty(item.variantId);
    if (next < 0) next = 0;
    if (next === cur) return;

    /* Card path: mirror the touched size onto the card's hidden pills so the card's
       own ATC pending state keys off the same variant it always did. */
    if (sheetCtx.sizId && item.pill) window.mcclSelectSize(item.pill, sheetCtx.sizId);

    /* add() is the +1 path (cap-checked, queued, optimistic); set() covers −1 and
       any jump. Both mutate Monocore.cart state synchronously, then emit
       monocore:cart:optimistic — which sheetSync() below turns into a re-render. */
    var op = (next === cur + 1) ? c.add(item.variantId) : c.set(item.variantId, next);
    if (op && typeof op.catch === 'function') op.catch(function () { /* toast already shown */ });

    if (next > cur) sheetWatchDrawer();

    sheetSync();
  }

  // ── Rendering ────────────────────────────────────────────────────
  function sheetLowStockThreshold() {
    var root = sheetRoot();
    var t = root ? parseInt(root.getAttribute('data-low-stock'), 10) : NaN;
    return isNaN(t) ? 3 : t;
  }

  function sheetBuildLeft(item, qty) {
    var thresh = sheetLowStockThreshold();
    var left = document.createElement('span');
    left.className = 'mcsz__left';
    var sz = document.createElement('span');
    sz.className = 'mcsz__sz';
    sz.textContent = item.label;
    left.appendChild(sz);

    var meta = '';
    if (!item.available) meta = 'Out of stock';
    else if (qty > 0 && item.max > 0 && qty >= item.max) meta = 'Max ' + item.max;
    else if (item.max > 0 && item.max <= thresh) meta = 'Only ' + item.max + ' left';
    if (meta) {
      var st = document.createElement('span');
      st.className = 'mcsz__st';
      st.textContent = meta;
      left.appendChild(st);
    }
    return left;
  }

  function sheetBuildStepper(item, qty) {
    var wrap = document.createElement('span');
    wrap.className = 'mcsz__step';

    var minus = document.createElement('button');
    minus.type = 'button';
    minus.textContent = '−';
    minus.setAttribute('aria-label', 'Decrease quantity, size ' + item.label);
    minus.addEventListener('click', function () { sheetSetQty(item, sheetQty(item.variantId) - 1); });

    var num = document.createElement('span');
    num.className = 'mcsz__n';
    num.textContent = qty;

    var plus = document.createElement('button');
    plus.type = 'button';
    plus.textContent = '+';
    plus.setAttribute('aria-label', 'Increase quantity, size ' + item.label);
    plus.disabled = !item.available;
    plus.addEventListener('click', function () { sheetSetQty(item, sheetQty(item.variantId) + 1); });

    wrap.appendChild(minus); wrap.appendChild(num); wrap.appendChild(plus);
    var n = sheetCtx && sheetCtx.nodes[item.variantId];
    if (n) { n.num = num; n.minus = minus; n.plus = plus; }
    return wrap;
  }

  function sheetBuildRow(item) {
    var qty = sheetQty(item.variantId);
    var row = document.createElement('div');
    row.className = 'mcsz__row' + (item.available ? '' : ' is-oos')
                  + (item.available && item.max > 0 && item.max <= sheetLowStockThreshold() ? ' is-low' : '')
                  + (qty > 0 ? ' is-on' : '');

    var left = sheetBuildLeft(item, qty);
    row.appendChild(left);
    sheetCtx.nodes[item.variantId] = { row: row, left: left, num: null, minus: null, plus: null };

    /* Unavailable AND nothing in the bag → nothing to control. Unavailable WITH
       units in the bag (added before it sold out) still gets a stepper so the
       shopper can take it out — the footer counts it either way. */
    if (!item.available && qty === 0) return row;

    if (qty > 0) {
      row.appendChild(sheetBuildStepper(item, qty));
    } else {
      var add = document.createElement('button');
      add.type = 'button';
      add.className = 'mcsz__go';
      add.textContent = 'ADD →';
      add.setAttribute('aria-label', 'Add size ' + item.label);
      add.addEventListener('click', function () { sheetSetQty(item, 1); });
      row.appendChild(add);
      /* mouse/touch convenience: anywhere on the row adds, same as the ADD button.
         .mcsz__left is pointer-events:none, so its taps retarget to the row. */
      row.addEventListener('click', function (e) { if (e.target === row) add.click(); });
    }
    return row;
  }

  function sheetRenderRows() {
    var rowsEl = document.getElementById('mcsz-rows');
    if (!rowsEl || !sheetCtx) return;
    rowsEl.textContent = '';
    sheetCtx.nodes = {};
    sheetCtx.items.forEach(function (item) { rowsEl.appendChild(sheetBuildRow(item)); });
  }

  /* In-place refresh. Rebuilding the whole list on every quantity change would
     destroy the button under the shopper's thumb — fast repeat taps get dropped
     and focus is lost. Only a row that changes SHAPE (ADD ↔ stepper) is rebuilt. */
  function sheetSyncRows() {
    if (!sheetCtx) return;
    var c = cartApi();
    sheetCtx.items.forEach(function (item) {
      var n = sheetCtx.nodes[item.variantId];
      if (!n || !n.row.parentNode) return;
      var qty = sheetQty(item.variantId);
      var removing = !!(c && c.isRemoving && c.isRemoving(item.variantId));
      var hasStepper = !!n.num;

      /* Shape change (ADD ↔ stepper) is the only case that rebuilds a row. Carry
         focus across it — otherwise a keyboard user is dumped on <body> the moment
         they add a size. */
      if ((qty > 0) !== hasStepper) {
        sheetSettle();
        var hadFocus = n.row.contains(document.activeElement);
        var fresh = sheetBuildRow(item);
        n.row.parentNode.replaceChild(fresh, n.row);
        if (hadFocus) {
          var nn = sheetCtx.nodes[item.variantId];
          var target = (nn && nn.plus && !nn.plus.disabled) ? nn.plus
                     : (nn && nn.minus) ? nn.minus
                     : fresh.querySelector('.mcsz__go');
          if (target && typeof target.focus === 'function') target.focus();
        }
        return;
      }

      n.row.classList.toggle('is-on', qty > 0);
      var left = sheetBuildLeft(item, qty);
      n.row.replaceChild(left, n.left);
      n.left = left;
      if (n.num) {
        n.num.textContent = qty;
        /* While a removal is in flight the optimistic qty deliberately stays put
           (see Monocore.cart.set) — freeze the control instead of inviting a
           second tap that would queue a duplicate request. */
        n.minus.disabled = removing || qty <= 0;
        n.plus.disabled = removing || !item.available || (item.max > 0 && qty >= item.max);
      }
    });
  }

  /* Footer structure is built ONCE per open and then only has its text updated.
     Two reasons: an aria-live region has to exist before its content changes or
     screen readers stay silent, and rebuilding the CTAs on every cart event threw
     away keyboard focus (there are three events per add). */
  function sheetBuildFoot() {
    var footEl = document.getElementById('mcsz-foot');
    if (!footEl || !sheetCtx) return;
    footEl.textContent = '';
    footEl.classList.remove('is-in');
    footEl.setAttribute('hidden', 'hidden');

    var sum = document.createElement('div');
    sum.className = 'mcsz__sum';
    sum.setAttribute('aria-live', 'polite');
    var count = document.createElement('b');
    var price = document.createElement('span');
    sum.appendChild(count);
    sum.appendChild(price);

    var btns = document.createElement('div');
    btns.className = 'mcsz__btns';
    var keep = document.createElement('button');
    keep.type = 'button';
    keep.className = 'mcsz__cta mcsz__cta--ghost';
    keep.textContent = 'KEEP SHOPPING';
    keep.addEventListener('click', function () { sheetClose('dismiss'); });
    var bag = document.createElement('button');
    bag.type = 'button';
    bag.className = 'mcsz__cta';
    bag.textContent = 'VIEW BAG \u2192';
    bag.addEventListener('click', function () { sheetClose('bag'); });
    btns.appendChild(keep);
    btns.appendChild(bag);

    footEl.appendChild(sum);
    footEl.appendChild(btns);
    sheetCtx.foot = { el: footEl, count: count, price: price };
  }

  /* The sheet is bottom-anchored: revealing the footer grows it upward and shifts
     every row. Two mitigations, because a shopper who taps twice quickly must not
     add the wrong size: the footer EXPANDS over ~240ms (CSS) instead of appearing,
     and sheetSettle() swallows control taps while it moves. */
  function sheetToggleFoot(show) {
    if (!sheetCtx || !sheetCtx.foot) return;
    var footEl = sheetCtx.foot.el;
    var isHidden = footEl.hasAttribute('hidden');
    if (show === !isHidden) return;
    if (show) {
      footEl.removeAttribute('hidden');
      /* Flush layout so max-height:0 is the transition's start value. Deliberately
         a synchronous reflow rather than requestAnimationFrame: rAF does not run in
         a backgrounded tab, and the footer must never be left collapsed (no bag
         summary, no CTAs) just because the shopper switched apps mid-add. Worst
         case here is an un-animated reveal, never a missing one. */
      void footEl.offsetHeight;
      footEl.classList.add('is-in');
      sheetSettle();
    } else {
      footEl.classList.remove('is-in');
      footEl.setAttribute('hidden', 'hidden');
      sheetSettle();
    }
  }

  /* Ignore control taps for one layout-settling window. Cheap insurance against a
     double-tap landing on whichever row slid under the finger. */
  function sheetSettle() {
    if (sheetCtx) sheetCtx.settleUntil = Date.now() + 320;
  }
  function sheetSettling() {
    return !!(sheetCtx && sheetCtx.settleUntil && Date.now() < sheetCtx.settleUntil);
  }

  function sheetRenderFoot() {
    if (!sheetCtx || !sheetCtx.foot) return;

    var units = 0, total = 0, compare = 0, haveCompare = true;
    var cmap = (window.Monocore && window.Monocore.compareMap) || {};
    sheetCtx.items.forEach(function (item) {
      var q = sheetQty(item.variantId);
      if (!q) return;
      units += q;
      total += q * (item.price || 0);
      var c = cmap[String(item.variantId)];
      if (c) compare += q * c; else haveCompare = false;
    });

    /* Nothing from this product in the bag → no footer at all. First-open state is
       byte-identical to the single-tap era. */
    if (!units) { sheetToggleFoot(false); return; }

    var f = sheetCtx.foot;
    f.count.textContent = units + (units === 1 ? ' item' : ' items');
    f.price.textContent = '';
    if (total > 0) {
      f.price.appendChild(document.createTextNode(sheetMoney(total)));
      if (haveCompare && compare > total) {
        var st = document.createElement('s');
        st.textContent = sheetMoney(compare);
        f.price.appendChild(st);
      }
    }
    sheetToggleFoot(true);
  }

  function sheetSync() { sheetSyncRows(); sheetRenderFoot(); }

  function sheetRender() { sheetRenderRows(); sheetRenderFoot(); }

  /* Cart truth (or a rejected/settled optimistic op) landed — reflect it. */
  function sheetOnCartEvent() { if (sheetIsOpen() && sheetCtx) sheetSync(); }
  document.addEventListener('monocore:cart:updated', sheetOnCartEvent);
  document.addEventListener('monocore:cart:optimistic', sheetOnCartEvent);

  // ── Shared opener ────────────────────────────────────────────────
  function sheetShow(ctx) {
    var root = sheetRoot();
    if (!root || !ctx.items.length) return false;

    ctx.nodes = {};
    ctx.drawerWasOpen = drawerIsOpen();
    sheetCtx = ctx;
    var rowsEl = document.getElementById('mcsz-rows');
    if (rowsEl) rowsEl.style.maxHeight = '';
    ctx.settleUntil = 0;
    sheetBuildFoot();

    /* Register caps so Monocore.cart can enforce them locally (card path already
       does this at init; the upsell path has no caps and passes -1). */
    var c = cartApi();
    if (c) ctx.items.forEach(function (i) {
      if (i.max === undefined || i.max === null) return;
      /* Never downgrade a known cap to "unlimited": the upsell path has no
         inventory data (products.json omits it) and passes -1, and the same
         variant may already be capped from a product card on this page. */
      if (i.max === -1 && c.getMax(i.variantId) !== undefined) return;
      c.setMax(i.variantId, i.max);
    });

    sheetRender();
    /* Pull cart truth: quantities may have been changed inside the KwikCart
       drawer, which our module never sees. Renders immediately from known state,
       then re-syncs in place when /cart.js lands. */
    if (c) c.refresh();
    root.classList.add('is-open');
    /* Opened over an already-open drawer (upsell) → elevate straight away. */
    if (ctx.elevate || ctx.drawerWasOpen) root.classList.add('mcsz--over-drawer');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('mcsz-lock');
    document.body.classList.add('mcsz-lock');
    var x = root.querySelector('.mcsz__x');
    if (x) x.focus();
    return true;
  }

  function sheetSetHeader(thumbSrc, title, priceText, compareText) {
    var thumb = document.getElementById('mcsz-thumb');
    if (thumb) {
      if (thumbSrc) { thumb.src = thumbSrc; thumb.style.display = ''; }
      else { thumb.removeAttribute('src'); thumb.style.display = 'none'; }
      thumb.alt = title || '';
    }
    var titleEl = document.getElementById('mcsz-title');
    if (titleEl) titleEl.textContent = title || '';
    var priceEl = document.getElementById('mcsz-price');
    if (priceEl) {
      priceEl.textContent = '';
      if (priceText) priceEl.appendChild(document.createTextNode(priceText));
      if (compareText) {
        var s = document.createElement('s');
        s.textContent = compareText;
        priceEl.appendChild(s);
      }
    }
  }

  // ── Entry point 1: product cards ─────────────────────────────────
  window.mcclOpenSizeSheet = function (addId, sizId) {
    var root = sheetRoot();
    var sizesWrap = document.getElementById(sizId);
    var pills = sizesWrap ? sizesWrap.querySelectorAll('.mccl__size') : [];
    if (!root || !pills.length) {
      /* No sheet on the page or a size-less product — legacy direct-add path. */
      window.mcclAddToCart(addId, sizId);
      return;
    }
    var card = sizesWrap.closest('.mccl__card');

    /* Header: thumb + title + price (+ struck MRP) from the card's DOM.
       textContent only — never innerHTML from card-derived strings. */
    var img = card ? card.querySelector('.mccl__img') : null;
    var nameEl = card ? card.querySelector('.mccl__name') : null;
    var p = card ? card.querySelector('.mccl__price') : null;
    var cmp = card ? card.querySelector('.mccl__compare') : null;
    sheetSetHeader(
      img ? (img.currentSrc || img.src) : '',
      nameEl ? nameEl.textContent.trim() : '',
      p ? p.textContent.trim() : '',
      cmp ? cmp.textContent.trim() : ''
    );

    /* Rows from the hidden pills. data-max: -1 = untracked, 0 = out of stock
       (also flagged .is-unavailable), 1..threshold = low-stock hint. */
    var items = [];
    Array.prototype.forEach.call(pills, function (pill) {
      var max = parseInt(pill.getAttribute('data-max'), 10);
      if (isNaN(max)) max = -1;
      items.push({
        label: pill.textContent.trim(),
        variantId: pill.getAttribute('data-variant-id'),
        available: !pill.classList.contains('is-unavailable'),
        max: max,
        price: parseInt(pill.getAttribute('data-price'), 10) || 0,
        pill: pill
      });
    });

    sheetShow({
      items: items,
      addId: addId,
      sizId: sizId,
      opener: document.getElementById(addId)
    });
  };

  // ── Entry point 2: data-driven (KwikCart upsell cards) ───────────
  /* Same sheet, no product-card DOM to scrape. Returns false if the sheet is
     absent or the product has no sizes, so the caller can fall back to a direct
     add. Quantity goes through Monocore.cart exactly as on the card path — the
     app's fetch patch sees the same /cart/add.js and re-renders the drawer. */
  window.mcszOpenData = function (data) {
    var root = sheetRoot();
    var sizes = ((data && data.sizes) || []).filter(Boolean);
    if (!root || !sizes.length) return false;

    sheetSetHeader(data.thumb, data.title, data.priceText, data.compareText);

    var items = sizes.map(function (v) {
      return {
        label: v.label,
        variantId: v.variantId,
        available: v.available !== false,
        max: (typeof v.max === 'number') ? v.max : -1,
        price: (typeof v.price === 'number') ? v.price : 0,
        pill: null
      };
    });

    return sheetShow({
      items: items,
      opener: data.opener || null,
      elevate: !!data.elevate
    });
  };

  /* Close wiring: backdrop + ✕ share [data-mcsz-close]; Escape while open. */
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('[data-mcsz-close]')) sheetClose('dismiss');
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sheetIsOpen()) sheetClose('dismiss');
  });

  // ── In-card image gallery (mobile swipe pip indicator) ──────────
  function setupCardGallery(slides) {
    if (slides.dataset.galleryReady === '1') return;
    slides.dataset.galleryReady = '1';
    if (!('IntersectionObserver' in window)) return;
    var dotsEl = slides.parentElement.querySelector('.mccl__img-dots');
    if (!dotsEl) return; /* only one image — no pips, nothing to track */
    var slideEls = slides.querySelectorAll('.mccl__slide');
    var dots = dotsEl.querySelectorAll('.mccl__img-dot');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting || e.intersectionRatio < 0.6) return;
        var idx = Array.prototype.indexOf.call(slideEls, e.target);
        if (idx < 0) return;
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
      });
    }, { root: slides, threshold: 0.6 });
    slideEls.forEach(function (s) { io.observe(s); });
  }

  // ── Init ─────────────────────────────────────────────────────────
  function init() {
    if (!document.querySelector('.mccl__card')) return; /* no cards on this page */
    if (!window.Monocore || !window.Monocore.cart) {
      /* Cart helper not yet loaded — defer. The render flushes on monocore:cart:updated anyway. */
      document.addEventListener('monocore:cart:updated', renderAllCards);
      document.addEventListener('monocore:cart:optimistic', renderAllCards);
      document.querySelectorAll('.mccl__slides').forEach(setupCardGallery);
      return;
    }
    document.querySelectorAll('.mccl__size').forEach(function (b) {
      var v = b.getAttribute('data-variant-id');
      var m = b.getAttribute('data-max');
      if (v && m !== null) window.Monocore.cart.setMax(v, m);
    });
    document.addEventListener('monocore:cart:updated', renderAllCards);
    document.addEventListener('monocore:cart:optimistic', renderAllCards);
    renderAllCards();
    document.querySelectorAll('.mccl__slides').forEach(setupCardGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ── Public API for sections that append cards dynamically ───────
  window.Monocore.card = {
    renderAll: renderAllCards,
    setupGallery: setupCardGallery,
    /* registerSizeCaps: walk a container's .mccl__size buttons and register
       inventory caps with Monocore.cart. Used by collection's load-more after
       AJAX append, and could be used by any other dynamic-card section. */
    registerSizeCaps: function (container) {
      if (!container || !window.Monocore || !window.Monocore.cart) return;
      container.querySelectorAll('.mccl__size').forEach(function (b) {
        var v = b.getAttribute('data-variant-id');
        var m = b.getAttribute('data-max');
        if (v && m !== null) window.Monocore.cart.setMax(v, m);
      });
    }
  };

  // ── Wishlist (v1: localStorage-only; cross-device sync deferred) ──────
  // Storage: localStorage key `mc_wishlist`, JSON-encoded array of product
  //   IDs (stringified). Survives page reloads and tab close/reopen.
  // Public events:
  //   document → 'monocore:wishlist:updated' { detail: { list, action?, id? } }
  //   fired after every mutation. Cross-tab `storage` events re-fire it.
  // Public API:
  //   list(), has(id), count(), add(id, meta), remove(id, meta),
  //   toggle(id, meta) → returns new state (true=added, false=removed),
  //   subscribe(fn) → returns unsubscribe.
  // `meta` (optional): { productTitle, price, currency } — used for analytics.
  var WL_KEY = 'mc_wishlist';

  function _wlRead() {
    try {
      var raw = localStorage.getItem(WL_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.map(String) : [];
    } catch (e) { return []; }
  }
  function _wlWrite(arr, action, id) {
    try { localStorage.setItem(WL_KEY, JSON.stringify(arr)); }
    catch (e) { /* quota exceeded — silent */ }
    document.dispatchEvent(new CustomEvent('monocore:wishlist:updated', {
      detail: { list: arr, action: action || null, id: id != null ? String(id) : null }
    }));
  }
  function _wlAnalytics(eventName, id, meta) {
    /* Shopify Web Pixels API — events flow through Shopify's pixel manager
       to all configured pixels (Shopify default + GA4/Meta if connected). */
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        var payload = {
          productId: String(id),
          productTitle: (meta && meta.productTitle) ? String(meta.productTitle) : '',
          /* Shopify amounts are stored in cents; divide for display value. */
          price: (meta && meta.price != null && !isNaN(Number(meta.price))) ? (Number(meta.price) / 100) : null,
          currency: (meta && meta.currency) ? String(meta.currency) : 'INR'
        };
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch (e) { /* never break UX on analytics failure */ }
  }

  window.Monocore.wishlist = {
    list: _wlRead,
    has: function (id) { return _wlRead().indexOf(String(id)) >= 0; },
    count: function () { return _wlRead().length; },
    add: function (id, meta) {
      var sid = String(id);
      var arr = _wlRead();
      if (arr.indexOf(sid) < 0) {
        arr.push(sid);
        _wlWrite(arr, 'add', sid);
        _wlAnalytics('add_to_wishlist', sid, meta);
      }
    },
    remove: function (id, meta) {
      var sid = String(id);
      var arr = _wlRead();
      var i = arr.indexOf(sid);
      if (i >= 0) {
        arr.splice(i, 1);
        _wlWrite(arr, 'remove', sid);
        _wlAnalytics('remove_from_wishlist', sid, meta);
      }
    },
    toggle: function (id, meta) {
      if (this.has(id)) { this.remove(id, meta); return false; }
      this.add(id, meta); return true;
    },
    subscribe: function (fn) {
      document.addEventListener('monocore:wishlist:updated', fn);
      return function () { document.removeEventListener('monocore:wishlist:updated', fn); };
    }
  };

  // Click handler called from inline onclick on every heart button.
  window.mcclWishToggle = function (event, btn) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    var pid = btn.getAttribute('data-product-id');
    if (!pid) return;
    var meta = {
      productTitle: btn.getAttribute('data-product-title') || '',
      price: btn.getAttribute('data-product-price'),
      currency: btn.getAttribute('data-product-currency') || 'INR'
    };
    var nowActive = window.Monocore.wishlist.toggle(pid, meta);
    btn.classList.toggle('is-active', nowActive);
    btn.setAttribute('aria-pressed', String(nowActive));
    btn.setAttribute('aria-label', nowActive ? 'Remove from wishlist' : 'Save to wishlist');
  };

  // Sync heart buttons (active state) and header count whenever wishlist changes.
  function _wlSyncHearts() {
    var ids = _wlRead();
    document.querySelectorAll('[data-mc-wish-btn]').forEach(function (btn) {
      var pid = btn.getAttribute('data-product-id');
      var active = ids.indexOf(String(pid)) >= 0;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
      btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Save to wishlist');
    });
  }
  function _wlSyncHeaderCount() {
    var el = document.getElementById('mcnv-wish-count');
    if (!el) return;
    var n = _wlRead().length;
    el.textContent = String(n);
    el.style.display = n > 0 ? '' : 'none';
    el.setAttribute('aria-label', n + (n === 1 ? ' item in wishlist' : ' items in wishlist'));
  }
  document.addEventListener('monocore:wishlist:updated', function () {
    _wlSyncHearts();
    _wlSyncHeaderCount();
  });

  // Cross-tab sync: when localStorage changes in another tab, re-fire the event.
  window.addEventListener('storage', function (e) {
    if (e.key === WL_KEY) {
      document.dispatchEvent(new CustomEvent('monocore:wishlist:updated', {
        detail: { list: _wlRead(), action: 'cross-tab', id: null }
      }));
    }
  });

  // Run initial sync after DOM is ready (the existing init() runs at the same point).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { _wlSyncHearts(); _wlSyncHeaderCount(); });
  } else {
    _wlSyncHearts();
    _wlSyncHeaderCount();
  }
})();
/* deploy: v26 size sheet multi-add — stays open, qty steppers, bag footer (2026-08-25) */
